// ============================================================
// EngineFactory — select the right engine based on platform & config
// ============================================================
import type { ResolvedConfig, PlatformCapabilities } from '../core/types';
import type { EngineContext, IEngine } from './IEngine';
import { HlsEngine } from './HlsEngine';
import { LLHlsEngine } from './LLHlsEngine';
import { NativeEngine } from './NativeEngine';

/** Direct file extensions — fallback detection when no explicit mp4 key */
const DIRECT_FILE_RE = /\.(mp4|webm|ogg)(\?|$)/i;

export function isDirectFileSource(config: ResolvedConfig): boolean {
  // Explicit mp4 key in sources — preferred way to signal direct file playback
  if (config.sources?.mp4) return true;
  // Fallback: detect by file extension on master/source URL
  const url = config.sources?.master || config.sources?.source || '';
  return DIRECT_FILE_RE.test(url);
}

export function createEngine(
  config: ResolvedConfig,
  capabilities: PlatformCapabilities,
  ctx: EngineContext,
  detectedLowLatency?: boolean,
): IEngine {
  // Direct file (MP4/WebM/Ogg) → native <video> element, no hls.js
  if (isDirectFileSource(config)) {
    ctx.logger.info('Engine: NativeEngine (direct file source)');
    return new NativeEngine(ctx);
  }

  // Mobile without MSE → native HLS (lightweight)
  if (capabilities.isNativeHLS && !capabilities.isMSE) {
    ctx.logger.info('Engine: HlsEngine (no MSE)');
    return new HlsEngine(ctx);
  }

  // Determine low-latency mode:
  // - config.lowLatency === true  → forced LL-HLS
  // - config.lowLatency === false → forced standard HLS
  // - config.lowLatency === 'auto' → use detected value
  const isLL = config.lowLatency === true
    || (config.lowLatency === 'auto' && detectedLowLatency === true);

  if (isLL) {
    ctx.logger.info('Engine: LLHlsEngine (low-latency)');
    return new LLHlsEngine(ctx);
  }

  // Default: hls.js
  ctx.logger.info('Engine: HlsEngine (standard)');
  return new HlsEngine(ctx);
}
